import * as _ from 'underscore'


import { USER_SESSION_SERVICE_NAME, UserSessionService } from "../../../../services/user-session"
import { GridService, GRID_SERVICE_NAME } from '../../../../services/grid'
import { Enums } from '../../../../models/common'
import { MeasureCollection } from '../../../../models/custom'
import { ColumnModel, FieldModel, MeasurementModel, MeasurementsRequestModel } from '../../../../modules/api/api.models'
import { Measure } from '../../../../models/custom/measure'
import { ConfigHelper, DataTypeGroupComponent, Dictionary, LocalizationHelper, ReferenceTypeComponent } from '../../../../helpers'
import { OpData, OpDataType, OpDataTypeInGroup, OpDevice, OpLocation, OpMeter, OpMeterType, OpTask, OpView, OpViewColumn } from '../../../../models/core'
import { IReferenceType } from '../../../../models/util/interfaces'
import { MeasurementsService } from '../../measurements/measurements.service'
import { DetailsCorrelationFilterType, GridDetailsSettings } from './../grid-details.interfaces'
import { MeasurementDataType } from '../../measurements/measurements.controller'
import { DataProviderRequest } from '../../../../data-provider'

//#region Public Types

export const GRID_DETAILS_SERVICE_NAME = 'gridDetailsService'
export const GRID_DETAILS_SERVICE_PROVIDER_NAME = 'gridDetailsServiceProvider'

//#endregion

export class GridDetailsService {

    public static get LOG_PREFIX(): string { return '[SIMAX.Module.Common.Grid-Details]' }

    // #region Ctor

    constructor(
        private readonly userSession: UserSessionService,
        private readonly gridService: GridService,
        private readonly measurementService: MeasurementsService
    ) { }

    // #endregion


    // #region Public Methods

    public getOptionsByReferenceType(referenceType: Enums.ReferenceType): GridDetailsSettings {
        switch (referenceType) {
            case Enums.ReferenceType.IdLocation:
                return <GridDetailsSettings>{
                    objects: [],
                    measurmentIdField: "idLocation",
                    measurmentIdFields: ["idLocation", "serialNbr", "idMeter"],
                    measurmentIdsFields: [
                        false, // use measurmentIdsField
                        {
                            fieldIdName: 'deviceIds',
                            objectName: 'devices'
                        },
                        {
                            fieldIdName: 'meterIds',
                            objectName: 'meters'
                        }
                    ],
                    measurmentIdSaveTo: 'locationsShortDetails',
                    measurmentIdsField: "locationIds",
                    eventNameForSchemaChanged: 'schemasInLocationsShortDetails',
                    filterId: DetailsCorrelationFilterType.Location,
                    refreshDelegate: null,
                    referenceType: referenceType,
                    moduleName: Enums.SimaxModuleName.Location
                }
            case Enums.ReferenceType.SerialNbr:
                return <GridDetailsSettings>{
                    objects: [],
                    measurmentIdField: "serialNbr",
                    measurmentIdsField: "deviceIds",
                    filterId: DetailsCorrelationFilterType.Device,
                    refreshDelegate: null,
                    referenceType: referenceType,
                    moduleName: Enums.SimaxModuleName.Device
                }
            case Enums.ReferenceType.IdMeter:
                return <GridDetailsSettings>{
                    objects: [],
                    measurmentIdField: "idMeter",
                    measurmentIdsField: "meterIds",
                    filterId: DetailsCorrelationFilterType.Meter,
                    refreshDelegate: null,
                    referenceType: referenceType,
                    moduleName: Enums.SimaxModuleName.Meter
                }
            case Enums.ReferenceType.IdTask:
                return <GridDetailsSettings>{
                    objects: [],
                    measurmentIdField: "taskId",
                    measurmentIdsField: "locationIds",
                    filterId: DetailsCorrelationFilterType.Location,
                    refreshDelegate: null,
                    referenceType: referenceType,
                    moduleName: Enums.SimaxModuleName.Task
                }
            default:
                return null
        }
    }

    public getPanelParamsToDisplayDataTypeGroup(moduleName: Enums.SimaxModuleName, referenceType: Enums.ReferenceType, referenceValue: number): JQueryPromise<OpDataTypeInGroup[]> {
        return DataTypeGroupComponent.getDataTypeInGroupByModule(
            this.userSession.getDataProvider(),
            Enums.SimaxDataTypeGroupCategory.WEBSIMAX__MODULE_NAME__PANEL_PARAMS_TO_DISPLAY,
            moduleName,
            referenceType,
            referenceValue
        )
    }

    public getPreviewPanelParamsDataTypeGroup(moduleName: Enums.SimaxModuleName, referenceType: Enums.ReferenceType, referenceValue: number): JQueryPromise<OpDataTypeInGroup[]> {
        return DataTypeGroupComponent.getDataTypeInGroupByModule(
            this.userSession.getDataProvider(),
            Enums.SimaxDataTypeGroupCategory.WEBSIMAX__MODULE_NAME__PREVIEW_PANEL_PARAMS,
            moduleName,
            referenceType,
            referenceValue
        )
    }

    public getMeasurementsDataTypeGroup(moduleName: Enums.SimaxModuleName, referenceType: Enums.ReferenceType, referenceValue: number): JQueryPromise<OpDataTypeInGroup[]> {

        return DataTypeGroupComponent.getDataTypeInGroupByModule(
            this.userSession.getDataProvider(),
            Enums.SimaxDataTypeGroupCategory.WEBSIMAX__MODULE_NAME__MEASUREMENTS,
            moduleName,
            referenceType,
            referenceValue
        )
    }

    public getMeasurementsColumnModels(moduleName: Enums.SimaxModuleName, referenceType: Enums.ReferenceType, referenceValue: number): JQueryPromise<ColumnModel[]> {
        const groupReferenceType: any = {
            groupReferenceType: referenceType,
            groupReferenceValue: referenceValue,
            skipView: true
        }

        return this.gridService.getColumnModelByCategory([
            Enums.SimaxDataTypeGroupCategory.WEBSIMAX__MODULE_NAME__MEASUREMENTS
        ],
            moduleName,
            groupReferenceType
        )
    }

    public getParametersDataTypeGroup(moduleName: Enums.SimaxModuleName, referenceType: Enums.ReferenceType, referenceValue: number): JQueryPromise<OpDataTypeInGroup[]> {
        return this.getPreviewPanelParamsDataTypeGroup(moduleName, referenceType, referenceValue)
            .then((previewDataTypes: OpDataTypeInGroup[]) => {
                if (_.size(previewDataTypes) > 0) {
                    return previewDataTypes
                }

                return this.getPanelParamsToDisplayDataTypeGroup(moduleName, referenceType, referenceValue)
                    .then((displayDataTypes: OpDataTypeInGroup[]) => {
                        return displayDataTypes
                    })
            })
    }

    public getPreviewColumns(moduleName: Enums.SimaxModuleName): JQueryPromise<ColumnModel[]> {
        const groupReferenceType: any = {
            groupReferenceType: Enums.ReferenceType.IdDistributor,
            groupReferenceValue: this.userSession.getLoggedOperator().idDistributor
        }

        return this.gridService.getColumnModelByCategory([
            Enums.SimaxDataTypeGroupCategory.WEBSIMAX__MODULE_NAME__PREVIEW_PANEL_PARAMS
        ],
            moduleName,
            groupReferenceType
        )
    }

    public getDisplayColumns(moduleName: Enums.SimaxModuleName): JQueryPromise<ColumnModel[]> {
        const groupReferenceType: any = {
            groupReferenceType: Enums.ReferenceType.IdDistributor,
            groupReferenceValue: this.userSession.getLoggedOperator().idDistributor
        }

        return this.gridService.getColumnModelByCategory([
            Enums.SimaxDataTypeGroupCategory.WEBSIMAX__MODULE_NAME__PANEL_PARAMS_TO_DISPLAY
        ],
            moduleName,
            groupReferenceType
        )
    }

    public getGridDetailsGridColumns(moduleName: Enums.SimaxModuleName): JQueryPromise<ColumnModel[]> {
        return this.getPreviewColumns(moduleName)
            .then((previewColumns: ColumnModel[]) => {
                if (_.size(previewColumns) > 0) {
                    return previewColumns
                }

                return this.getDisplayColumns(moduleName)
                    .then((displayColumns: ColumnModel[]) => {
                        return displayColumns
                    })
            })
    }

    public getMeasurements(idReference: Enums.ReferenceType, parameters: MeasurementsRequestModel): DataProviderRequest<MeasureCollection[]> {
        const deferred: JQueryDeferred<MeasureCollection[]> = $.Deferred<MeasureCollection[]>()

        let idLocations: number[] = null
        let idDevices: number[] = null
        let idMeters: number[] = null

        switch (idReference) {
            case Enums.ReferenceType.IdLocation:
                idLocations = parameters.locationIds
                idDevices = parameters.deviceIds
                idMeters = parameters.meterIds
                break
            case Enums.ReferenceType.SerialNbr:
                idDevices = parameters.deviceIds
                break
            case Enums.ReferenceType.IdMeter:
                idMeters = parameters.meterIds
                break
            default:
                deferred.resolve([])
                return DataProviderRequest.fromJQuery(deferred.promise())
        }

        const promise: JQueryPromise<MeasureCollection[]> = this.userSession
            .getDataProvider()
            .Measurements
            .getMeasurementsFilter(idLocations, idDevices, idMeters, _.map(parameters.columns, (column: ColumnModel) => column.id), parameters.dateFrom, parameters.dateTo)
            .then((result: MeasureCollection[]) => {
                return result
            })
            .fail(() => {
                return []
            })

        return DataProviderRequest.fromJQuery(promise)
    }


    public getReferenceObject(idReference: Enums.ReferenceType, idToGet: number): DataProviderRequest<IReferenceType> {
        switch (idReference) {
            case Enums.ReferenceType.IdLocation:
                return this.userSession
                    .getDataProvider()
                    .Location
                    .getLocation(idToGet)
            case Enums.ReferenceType.SerialNbr:
                return this.userSession
                    .getDataProvider()
                    .Device
                    .getDevice(idToGet)
            case Enums.ReferenceType.IdMeter:
                return this.userSession
                    .getDataProvider()
                    .Meter
                    .getMeter(idToGet)
            case Enums.ReferenceType.IdTask: //is this used at all?
                return this.userSession
                    .getDataProvider()
                    .Task
                    .getTask(idToGet)
            default:
                const deferred: JQueryDeferred<any> = $.Deferred<any>()
                deferred.resolve({})
                return DataProviderRequest.fromJQuery(deferred.promise())
        }
    }

    /**@deprecated just part of code from thin client to avoid thin client request */
    public referenceObjectIntoModel(opObj: IReferenceType) {
        switch (opObj.getReferenceType()) {
            case Enums.ReferenceType.IdLocation:
                {
                    const location: OpLocation = <OpLocation>opObj
                    const retVal = {
                        id: location.idLocation,
                        name: location.toString(),
                        referenceId: location.idDistributor,
                        devices: _.map(location.currentDevices, (device: OpDevice) => {
                            return {
                                id: device.serialNbr,
                                name: device.toString(),
                                referenceId: device.idDeviceType,
                            }
                        }),
                        meters: _.map(location.currentMeters, (meter: OpMeter) => {
                            return {
                                id: meter.idMeter,
                                name: meter.toString(),
                                referenceId: meter.idMeterType,
                                fields: []
                            }
                        }),
                        fields: []
                    }

                    const fieldsPromises: JQueryPromise<FieldModel>[] = _.map(location.dynamicValues, (data: any) => this.getFieldFromDynamicValue(data, location.getReferenceType()))

                    return $.when(...fieldsPromises)
                        .then((...fields: FieldModel[]) => {
                            retVal.fields = fields

                            return retVal
                        })
                }
            case Enums.ReferenceType.SerialNbr:
                {
                    const device: OpDevice = <OpDevice>opObj
                    const retVal = {
                        id: device.serialNbr,
                        referenceId: device.getReferenceType(),
                        locations: _.map(device.currentLocations, (location: OpLocation) => {
                            return {
                                id: location.idLocation,
                                name: location.toString(),
                                referenceId: location.idDistributor,
                            }
                        }),
                        meters: _.map(device.currentMeters, (meter: OpMeter) => {
                            return {
                                id: meter.idMeter,
                                name: meter.toString(),
                                referenceId: meter.idMeterType,
                            }
                        }),
                        fields: []
                    }

                    const fieldsPromises: JQueryPromise<FieldModel>[] = _.map(device.dynamicValues, (data: any) => this.getFieldFromDynamicValue(data, device.getReferenceType()))

                    return $.when(...fieldsPromises)
                        .then((...fields: FieldModel[]) => {
                            retVal.fields = fields

                            return retVal
                        })

                }
            case Enums.ReferenceType.IdMeter:
                {
                    const meter: OpMeter = <OpMeter>opObj
                    const retVal = {
                        id: meter.idMeter,
                        referenceId: meter.getReferenceType(),
                        locations: _.map(meter.currentLocations, (location: OpLocation) => {
                            return {
                                id: location.idLocation,
                                name: location.toString(),
                                referenceId: location.idDistributor
                            }
                        }),
                        devices: _.map(meter.currentDevices, (device: OpDevice) => {
                            return {
                                id: device.serialNbr,
                                name: device.toString(),
                                referenceId: device.idDeviceType
                            }
                        }),
                        fields: []
                    }

                    const fieldsPromises: JQueryPromise<FieldModel>[] = _.map(meter.dynamicValues, (data: any) => this.getFieldFromDynamicValue(data, meter.getReferenceType()))

                    return $.when(...fieldsPromises)
                        .then((...fields: FieldModel[]) => {
                            retVal.fields = fields

                            return retVal
                        })
                }
            case Enums.ReferenceType.IdTask:
                {
                    const task: OpTask = <OpTask>opObj
                    const retVal = {
                        id: task.idTask,
                        name: task.toString(),
                        idLocation: task.idLocation,
                        fields: []
                    }

                    const fieldsPromises: JQueryPromise<FieldModel>[] = _.map(task.dynamicValues, (data: any) => this.getFieldFromDynamicValue(data, task.getReferenceType()))

                    return $.when(...fieldsPromises)
                        .then((...fields: FieldModel[]) => {
                            retVal.fields = fields

                            return retVal
                        })
                }
            default:
                const deferred: JQueryDeferred<any> = $.Deferred<any>()
                deferred.resolve({})
                return deferred.promise()
        }
    }

    /**used for location only */
    public getMeasurementsTypeSelectBoxDataSource(location: OpLocation) {
        const deviceTypeIds: number[] = location.currentDevices.map((device: OpDevice) => device.idDeviceType).filter(this.onlyUnique)
        const meterTypesIds: number[] = location.currentMeters.map((meter: OpMeter) => meter.idMeterType).filter(this.onlyUnique)

        return $.when(
            this.getMeasurementsDataTypeGroup(Enums.SimaxModuleName.Location, Enums.ReferenceType.IdDistributor, this.userSession.getLoggedOperator().idDistributor),
            ...deviceTypeIds.map((id: number) => this.getMeasurementsDataTypeGroup(Enums.SimaxModuleName.Device, Enums.ReferenceType.IdDeviceType, id)),
            ...meterTypesIds.map((id: number) => this.getMeasurementsDataTypeGroup(Enums.SimaxModuleName.Meter, Enums.ReferenceType.IdMeterType, id)),
        )
            .then((locationGroup: OpDataTypeInGroup[], deviceGroup: OpDataTypeInGroup[], meterGroup: OpDataTypeInGroup[]) => {
                const dataSource: { name: string, type: number, icon: ConfigHelper.Icon }[] = []

                if (_.size(locationGroup) > 0) {
                    dataSource.push({
                        name: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.CurrentLocation),
                        type: DetailsCorrelationFilterType.Location,
                        icon: ConfigHelper.Icon.LOCATIONS
                    })
                }

                if (_.size(deviceGroup) > 0) {
                    dataSource.push({
                        name: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Devices),
                        type: DetailsCorrelationFilterType.Device,
                        icon: ConfigHelper.Icon.DEVICES
                    })
                }

                if (_.size(meterGroup) > 0) {
                    dataSource.push({
                        name: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Meters),
                        type: DetailsCorrelationFilterType.Meter,
                        icon: ConfigHelper.Icon.METERS
                    })
                }

                return dataSource
            })
    }

    public getMeasurementsColumnsByReferenceType(reference: IReferenceType): JQueryPromise<ColumnModel[]> {
        const moduleName: Enums.SimaxModuleName = ReferenceTypeComponent.getModuleByReferenceType(reference.getReferenceType())

        switch (reference.getReferenceType()) {
            case Enums.ReferenceType.IdLocation:
                return this.getMeasurementsColumnModels(moduleName, Enums.ReferenceType.IdDistributor, this.userSession.getLoggedOperator().idDistributor)
            case Enums.ReferenceType.SerialNbr:
                return this.getMeasurementsColumnModels(moduleName, Enums.ReferenceType.IdDeviceType, (<OpDevice>reference).idDeviceType)
            case Enums.ReferenceType.IdMeter:
                if ((<OpMeter>reference).meterType != null) {
                    return this.getMeasurementsColumnModels(moduleName, Enums.ReferenceType.IdMeterTypeClass, (<OpMeter>reference).meterType.idMeterTypeClass)
                } else {
                    return this.userSession
                        .getDataProvider().System
                        .getDictionaries([Enums.ReferenceType.IdMeterType], true)
                        .then((dictionaries: Dictionary<Dictionary<IReferenceType>>) => {
                            const meterTypesDictionary: Dictionary<IReferenceType> = dictionaries.tryGetValue(Enums.ReferenceType.IdMeterType)
                            let idMeterTypeClass: number = 0

                            if (dictionaries != null && dictionaries.containsKey(Enums.ReferenceType.IdMeterType) && meterTypesDictionary != null) {
                                const meterType: OpMeterType = <OpMeterType>meterTypesDictionary.tryGetValue((<OpMeter>reference).idMeterType)

                                if (meterType != null) {
                                    idMeterTypeClass = meterType.idMeterTypeClass
                                }
                            }

                            return this.getMeasurementsColumnModels(moduleName, Enums.ReferenceType.IdMeterTypeClass, idMeterTypeClass)
                        })
                }
            default:
                const deferred: JQueryDeferred<ColumnModel[]> = $.Deferred<ColumnModel[]>()
                deferred.resolve([])
                return deferred.promise()
        }
    }

    public getReferenceTypeAndValueByReference(reference: IReferenceType): JQueryPromise<{ referenceType: Enums.ReferenceType, referenceValue: number }> {
        const deferred: JQueryDeferred<{ referenceType: Enums.ReferenceType, referenceValue: number }> = $.Deferred<{ referenceType: Enums.ReferenceType, referenceValue: number }>()

        switch (reference.getReferenceType()) {
            case Enums.ReferenceType.IdLocation:
                deferred.resolve({ referenceType: Enums.ReferenceType.IdDistributor, referenceValue: this.userSession.getLoggedOperator().idDistributor })
            case Enums.ReferenceType.SerialNbr:
                deferred.resolve({ referenceType: Enums.ReferenceType.IdDeviceType, referenceValue: (<OpDevice>reference).idDeviceType })
            case Enums.ReferenceType.IdMeter:
                if ((<OpMeter>reference).meterType != null) {
                    deferred.resolve({ referenceType: Enums.ReferenceType.IdMeterTypeClass, referenceValue: (<OpMeter>reference).meterType.idMeterTypeClass })
                } else {
                    this.userSession
                        .getDataProvider().System
                        .getDictionaries([Enums.ReferenceType.IdMeterType], true)
                        .then((dictionaries: Dictionary<Dictionary<IReferenceType>>) => {
                            const meterTypesDictionary: Dictionary<IReferenceType> = dictionaries.tryGetValue(Enums.ReferenceType.IdMeterType)
                            let idMeterTypeClass: number = 0

                            if (dictionaries != null && dictionaries.containsKey(Enums.ReferenceType.IdMeterType) && meterTypesDictionary != null) {
                                const meterType: OpMeterType = <OpMeterType>meterTypesDictionary.tryGetValue((<OpMeter>reference).idMeterType)

                                if (meterType != null) {
                                    idMeterTypeClass = meterType.idMeterTypeClass
                                }
                            }

                            deferred.resolve({ referenceType: Enums.ReferenceType.IdMeterTypeClass, referenceValue: idMeterTypeClass })
                        })
                }
            default:
                deferred.resolve({ referenceType: Enums.ReferenceType.IdDistributor, referenceValue: this.userSession.getLoggedOperator().idDistributor })
        }

        return deferred.promise()

    }

    public setChartType(measurementDataTypes: MeasurementDataType[]): JQueryPromise<MeasurementDataType[]> {
        if (_.size(measurementDataTypes) > 0) {
            return $.when(..._.map(measurementDataTypes, (measType: MeasurementDataType) => {
                return this.measurementService
                    .getChartTypeByIdDataType(measType.dataTypeId)
                    .then((type: Enums.ChartType) => {
                        if (type == null) {
                            type = Enums.ChartType.Line
                        }

                        measType.chartType = this.measurementService.chartTypeToDevExpressType(type)
                        return measType
                    })
            }))
                .then((...mdt: MeasurementDataType[]) => {
                    return mdt
                })
        }

        const deferred: JQueryDeferred<MeasurementDataType[]> = $.Deferred<MeasurementDataType[]>()
        deferred.resolve([])
        return deferred.promise()

    }

    public getThresHoldColums(): JQueryPromise<ColumnModel[]> {
        return this.measurementService
            .getThresholdColumns()
    }

    /**@deprecated */
    public getMeasurementModel(collection: MeasureCollection[]): MeasurementModel[] {
        const retModel: MeasurementModel[] = []

        _.forEach(collection, (res: MeasureCollection) => {
            retModel.push(...this.measurementModelFromOpDataArch(res))
        })

        return retModel
    }

    // #endregion


    // #region Private Methods    

    /**@deprecated */
    private measurementModelFromOpDataArch(op: MeasureCollection): MeasurementModel[] {
        const measures: MeasurementModel[] = []

        const dataTypes: OpDataType[] = this.userSession.getDataProvider().DataType.getDataTypesLocal([op.idDataType])

        if (_.size(dataTypes) > 0) {
            const type: Enums.DataTypeClass = dataTypes[0].idDataTypeClass

            _.forEach(op.measures, (mess: Measure) => {
                const date: Date = (mess.time != null) ? mess.time : new Date()

                let isDate: boolean = false
                let value: any = mess.value

                if (value != null) {
                    if (type == Enums.DataTypeClass.Date || type == Enums.DataTypeClass.Time || type == Enums.DataTypeClass.Datetime) {
                        isDate = true
                    }
                }

                const mMod: MeasurementModel = <MeasurementModel>{
                    dataType: op.idDataType,
                    date: date,
                    serialNbr: op.serialNbr,
                    idMeter: op.idMeter,
                    idLocation: op.idLocation,
                    value: value,
                    // isDate: isDate //was commented. dont know why
                }

                measures.push(mMod)
            })
        }

        return measures
    }

    /**@deprecated */
    private getFieldFromDynamicValue(data: any, referenceType: Enums.ReferenceType): JQueryPromise<FieldModel> {
        return this.userSession
            .getDataProvider()
            .View
            .getActiveView(referenceType)
            .then((view: OpView) => {
                const col: OpViewColumn = view.getViewColumnByName(data.Key)

                return <FieldModel>{
                    idDataType: col.idDataType,
                    name: data.Key.toLowerCase(),
                    value: data.Value
                }
            })
    }

    // #endregion

    private onlyUnique(value: any, index: number, self: any[]): boolean {
        return self.indexOf(value) === index;
    }
}

export class GridDetailsServiceProvider
    implements ng.IServiceProvider {

    //#region IServiceProvider Implementation 

    public $get = [
        USER_SESSION_SERVICE_NAME,
        GRID_SERVICE_NAME,
        'measurements',
        function (
            userSession: UserSessionService,
            gridService: GridService,
            measurementService: MeasurementsService
        ) {
            return new GridDetailsService(userSession, gridService, measurementService)
        }]

    //#endregion
}
